import { writable, derived } from 'svelte/store';
import { browser } from '$app/environment';

export interface BackgroundJob {
	id: string;
	type: string;
	status: 'waiting' | 'active' | 'completed' | 'failed';
	progress?: { percent?: number; message?: string };
	startedAt: number;
	orderCount?: number;
	failedReason?: string;
}

// Store for active background jobs
function createBackgroundJobsStore() {
	const { subscribe, set, update } = writable<Record<string, BackgroundJob>>({});

	// Load jobs from localStorage on initialization
	if (browser) {
		const stored = localStorage.getItem('backgroundJobs');
		if (stored) {
			try {
				const jobs = JSON.parse(stored);
				// Filter out old jobs (older than 1 hour)
				const oneHourAgo = Date.now() - 60 * 60 * 1000;
				const activeJobs = Object.fromEntries(
					Object.entries(jobs).filter(([, job]) => (job as BackgroundJob).startedAt > oneHourAgo)
				) as Record<string, BackgroundJob>;
				set(activeJobs);
			} catch (e) {
				console.error('Failed to parse stored background jobs:', e);
			}
		}
	}

	// Save to localStorage whenever jobs change
	subscribe((jobs) => {
		if (browser) {
			localStorage.setItem('backgroundJobs', JSON.stringify(jobs));
		}
	});

	async function checkJobStatus(jobId: string) {
		try {
			const response = await fetch(`/api/jobs/${jobId}/status`);
			if (!response.ok) {
				throw new Error('Failed to check job status');
			}

			const result = await response.json();

			update((jobs) => {
				if (result.status === 'not_found' || result.isCompleted || result.isFailed) {
					// Remove completed or not found jobs after a delay
					setTimeout(() => {
						update((j) => {
							const updated = { ...j };
							delete updated[jobId];
							return updated;
						});
					}, 5000); // Keep completed jobs visible for 5 seconds
				}

				if (jobs[jobId]) {
					return {
						...jobs,
						[jobId]: {
							...jobs[jobId],
							status: result.status,
							progress: result.progress,
							failedReason: result.failedReason,
						},
					};
				}
				return jobs;
			});

			return result;
		} catch (error) {
			console.error('Error checking job status:', error);
			return null;
		}
	}

	function addJob(jobId: string, type: string, orderCount?: number) {
		update((jobs) => ({
			...jobs,
			[jobId]: {
				id: jobId,
				type,
				status: 'waiting',
				startedAt: Date.now(),
				orderCount,
			},
		}));

		// Start polling for this job
		pollJob(jobId);
	}

	function pollJob(jobId: string) {
		const interval = setInterval(async () => {
			const result = await checkJobStatus(jobId);
			if (!result || result.isCompleted || result.isFailed || result.status === 'not_found') {
				clearInterval(interval);
			}
		}, 2000); // Poll every 2 seconds
	}

	function removeJob(jobId: string) {
		update((jobs) => {
			const updated = { ...jobs };
			delete updated[jobId];
			return updated;
		});
	}

	return {
		subscribe,
		addJob,
		removeJob,
		checkJobStatus,
	};
}

export const backgroundJobs = createBackgroundJobsStore();

// Derived store for active jobs count
export const activeJobsCount = derived(
	backgroundJobs,
	($jobs) =>
		Object.values($jobs).filter((job) => job.status === 'active' || job.status === 'waiting').length
);

// Derived store to check if any order completion jobs are running
export const hasActiveOrderJobs = derived(backgroundJobs, ($jobs) =>
	Object.values($jobs).some(
		(job) => job.type === 'complete_orders' && (job.status === 'active' || job.status === 'waiting')
	)
);
